import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import { Bell, Hash, ShieldAlert, CheckCircle, Info, ChevronLeft } from 'lucide-react';
import './SlackIntegration.css';

const SlackIntegration = () => {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { dialogState, closeDialog, alert: customAlert } = useDialog();
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [integrationId, setIntegrationId] = useState(null);
    const [workspaceName, setWorkspaceName] = useState('');
    const [connectedDate, setConnectedDate] = useState(null);
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    const [settings, setSettings] = useState({
        defaultSlackChannel: '',
        notifyOnPlanStart: false,
        notifyOnPlanCompletion: true,
        notifyOnPlanFailure: true,
        notifyOnGoalSuccess: false,
        notifyOnGoalFailure: true,
    });

    useEffect(() => {
        if (currentWorkspace) {
            checkStatus();
            fetchChannels();
        }
    }, [currentWorkspace]);

    const checkStatus = async () => {
        try {
            const response = await api.get('/integrations/slack/status', {
                params: { workspaceId: currentWorkspace.id }
            });

            if (response.data.connected) {
                setConnected(true);
                setWorkspaceName(response.data.workspaceName);
                setIntegrationId(response.data.id);
                setConnectedDate(response.data.connectedOn);

                if (response.data.settings) {
                    const s = response.data.settings;
                    setSettings({
                        defaultSlackChannel: s.defaultSlackChannel || '',
                        notifyOnPlanStart: s.notifyOnPlanStart !== undefined ? s.notifyOnPlanStart : false,
                        notifyOnPlanCompletion: s.notifyOnPlanCompletion !== undefined ? s.notifyOnPlanCompletion : true,
                        notifyOnPlanFailure: s.notifyOnPlanFailure !== undefined ? s.notifyOnPlanFailure : true,
                        notifyOnGoalSuccess: s.notifyOnGoalSuccess !== undefined ? s.notifyOnGoalSuccess : false,
                        notifyOnGoalFailure: s.notifyOnGoalFailure !== undefined ? s.notifyOnGoalFailure : true,
                    });
                }
            } else {
                setConnected(false);
            }
        } catch (error) {
            console.error('Error checking Slack status:', error);
            setConnected(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchChannels = async () => {
        setChannelsLoading(true);
        try {
            const { data } = await api.get('/integrations/slack/channels');
            setChannels(data.channels || []);
        } catch (err) {
            console.error('Failed to fetch Slack channels:', err);
            setChannels([]);
        } finally {
            setChannelsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!integrationId) return;

        setSaving(true);
        try {
            await api.patch(`/integrations/${integrationId}/settings`, {
                settings: settings
            });
            customAlert({
                title: 'Settings Saved',
                message: 'Workspace Slack defaults have been updated successfully.',
                variant: 'success'
            });
        } catch (error) {
            console.error('Error saving Slack settings:', error);
            customAlert({
                title: 'Save Failed',
                message: 'Failed to save settings. Please ensure Slack is connected and try again.',
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!settings.defaultSlackChannel) {
            customAlert({
                title: 'Selection Required',
                message: 'Please select a default channel first before testing.',
                variant: 'warning'
            });
            return;
        }

        setIsTesting(true);
        try {
            const response = await api.post('/integrations/slack/test', {
                channelId: settings.defaultSlackChannel
            });
            customAlert({
                title: 'Test Successful',
                message: response.data.message || 'Test message sent to Slack!',
                variant: 'success'
            });
        } catch (err) {
            customAlert({
                title: 'Test Failed',
                message: err.response?.data?.error || 'Failed to send test message. Please check your Slack connection.',
                variant: 'error'
            });
        } finally {
            setIsTesting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="slack-config-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading Slack configuration...</p>
                </div>
            </div>
        );
    }

    if (!connected) {
        return (
            <div className="slack-config-container">
                <div className="not-connected-state">
                    <div className="icon-wrapper">
                        <Hash size={48} />
                    </div>
                    <h2>Slack Not Connected</h2>
                    <p>Connect your Slack workspace to enable real-time notifications for your team.</p>
                    <button className="btn-primary btn" onClick={() => navigate('/settings/integrations')}>
                        Go to Integrations
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="slack-config-container">
            <div className="slack-config-header">
                <button className="back-nav" onClick={() => navigate('/settings/integrations')}>
                    <ChevronLeft size={20} /> Back to Integrations
                </button>
                <div className="title-section">
                    <h1>Slack Notifications</h1>
                    <div className="status-group">
                        <div className="status-badge connected">
                            <span className="dot"></span> Connected to {workspaceName}
                        </div>
                        {connectedDate && (
                            <span className="connection-date">
                                Since {formatDate(connectedDate)}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="slack-config-content">
                <div className="config-card">
                    <div className="card-header">
                        <Bell size={20} />
                        <h3>Workspace Notification Defaults</h3>
                    </div>
                    <div className="card-body">
                        <p className="description">
                            Set the default notification settings for all apps in this workspace.
                            Individual apps can override these settings in their respective "Integrations" tab.
                        </p>

                        <div className="form-section">
                            <div className="form-group">
                                <label>Default Notification Channel</label>
                                <div className="input-with-action">
                                    <select
                                        name="defaultSlackChannel"
                                        value={settings.defaultSlackChannel}
                                        onChange={handleChange}
                                        disabled={channelsLoading}
                                        className="form-select"
                                    >
                                        <option value="">Select a default channel...</option>
                                        {channels.map(ch => (
                                            <option key={ch.id} value={ch.id}>
                                                #{ch.name} {ch.is_private ? '🔒' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn-test"
                                        onClick={handleTest}
                                        disabled={isTesting || !settings.defaultSlackChannel}
                                    >
                                        {isTesting ? 'Testing...' : 'Test'}
                                    </button>
                                </div>
                                <div className="help-box">
                                    <Info size={14} />
                                    <span>
                                        Private channels only appear if the Rova bot has been invited via <code>/invite @Rova</code>.
                                    </span>
                                </div>
                            </div>

                            <div className="toggle-group">
                                <p className="toggle-group-label">Notify on:</p>

                                <div className="toggle-item">
                                    <div className="toggle-info">
                                        <Bell size={18} className="info-icon" />
                                        <div>
                                            <span className="toggle-label">Plan Start</span>
                                            <span className="toggle-desc">Announce when a test run begins. Replies thread under this message.</span>
                                        </div>
                                    </div>
                                    <label className="switch">
                                        <input type="checkbox" name="notifyOnPlanStart" checked={settings.notifyOnPlanStart} onChange={handleChange} />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="toggle-item">
                                    <div className="toggle-info">
                                        <CheckCircle size={18} className="success-icon" />
                                        <div>
                                            <span className="toggle-label">Plan Completion</span>
                                            <span className="toggle-desc">Summary when a plan finishes, regardless of result.</span>
                                        </div>
                                    </div>
                                    <label className="switch">
                                        <input type="checkbox" name="notifyOnPlanCompletion" checked={settings.notifyOnPlanCompletion} onChange={handleChange} />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="toggle-item">
                                    <div className="toggle-info">
                                        <ShieldAlert size={18} className="failure-icon" />
                                        <div>
                                            <span className="toggle-label">Plan Failures</span>
                                            <span className="toggle-desc">Alert when an entire plan run fails or errors out.</span>
                                        </div>
                                    </div>
                                    <label className="switch">
                                        <input type="checkbox" name="notifyOnPlanFailure" checked={settings.notifyOnPlanFailure} onChange={handleChange} />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="toggle-item">
                                    <div className="toggle-info">
                                        <CheckCircle size={18} className="goal-success-icon" />
                                        <div>
                                            <span className="toggle-label">Single Goal Success</span>
                                            <span className="toggle-desc">Thread reply for each passing goal. Off by default to reduce noise.</span>
                                        </div>
                                    </div>
                                    <label className="switch">
                                        <input type="checkbox" name="notifyOnGoalSuccess" checked={settings.notifyOnGoalSuccess} onChange={handleChange} />
                                        <span className="slider round"></span>
                                    </label>
                                </div>

                                <div className="toggle-item">
                                    <div className="toggle-info">
                                        <ShieldAlert size={18} className="failure-icon" />
                                        <div>
                                            <span className="toggle-label">Single Goal Failure</span>
                                            <span className="toggle-desc">Immediate alert when an individual goal fails, even mid-run.</span>
                                        </div>
                                    </div>
                                    <label className="switch">
                                        <input type="checkbox" name="notifyOnGoalFailure" checked={settings.notifyOnGoalFailure} onChange={handleChange} />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-footer">
                        <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Workspace Defaults'}
                        </button>
                    </div>
                </div>

                {/* <div className="hybrid-info-card">
                    <h4>About Managed Overrides</h4>
                    <p>
                        Rova uses a <strong>Hybrid Integration Model</strong>.
                        Workspace defaults are used unless an individual app specifies its own channel
                        or triggers.
                    </p>
                    <button className="btn-link" onClick={() => navigate('/apps')}>
                        Manage App Overrides →
                    </button>
                </div> */}
            </div>

            <Dialog {...dialogState} onClose={closeDialog} />
        </div>
    );
};

export default SlackIntegration;
